
import { useState, useEffect } from 'react';
import { useOnboardingContext } from '@/context/OnboardingContext';

// Types - onboarding steps order: info -> install -> email -> verification -> success
export type OnboardingStep = 'info' | 'install' | 'email' | 'verification' | 'success';

// Helper to check if app is in standalone mode
const checkIsStandalone = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  if ((navigator as any).standalone === true) {
    return true;
  }
  
  if (document.referrer.includes('android-app://')) {
    return true;
  }
  
  return false;
};

export const useOnboardingState = () => {
  const { loadUserData } = useOnboardingContext();
  const [step, setStep] = useState<OnboardingStep>('install');
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [expectedOtp, setExpectedOtp] = useState("");
  const [resendDisabled, setResendDisabled] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [emailError, setEmailError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Check if app just opened in standalone mode after install
  useEffect(() => {
    const isStandalone = checkIsStandalone();
    const pendingStep = localStorage.getItem('pendingOnboardingStep');
    
    if (isStandalone && pendingStep === 'email') {
      console.log('App opened in standalone mode with pending step, advancing to email');
      localStorage.removeItem('pendingOnboardingStep');
      setStep('email');
    }
  }, []);

  // Save current step to localStorage for persistence
  useEffect(() => {
    localStorage.setItem('currentOnboardingStep', step);
    console.log('Current onboarding step saved:', step);
  }, [step]);

  // Check for existing onboarding state on initial load
  useEffect(() => {
    // If the user is already on the 'success' step, we shouldn't reset the state
    if (step !== 'success') {
      // Get the existing app installation status
      const appInstalled = localStorage.getItem('appInstalled');
      if (appInstalled === 'true') {
        // Skip install step if app is already installed and we're on that step
        if (step === 'install') {
          setStep('email');
        }
      }
    }
  }, [step]);

  // Reset countdown timer when an OTP is sent
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (resendDisabled && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && resendDisabled) {
      setResendDisabled(false);
    }
    
    return () => clearTimeout(timer);
  }, [resendDisabled, countdown]);

  return {
    step,
    setStep,
    name,
    setName,
    email,
    setEmail,
    phoneNumber,
    setPhoneNumber,
    phoneError,
    setPhoneError,
    expectedOtp,
    setExpectedOtp,
    resendDisabled,
    setResendDisabled,
    countdown,
    setCountdown,
    emailError,
    setEmailError,
    isLoading,
    setIsLoading,
    loadUserData
  };
};
